import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetTeamQuery as GetTeam } from '__generated__/graphql';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import ContactPicker, { Participant } from '../Contacts/ContactPicker';
import EditBackendTeamMutation from './EditBackendTeamMutation';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useValidation from '../../../common/useValidation';

type Team = GetTeam['team'];

const editTeamSchema = yup.object({
  title: yup.string().min(1).max(100),
});

export interface EditTeamFormProps {
  team: Team;
  onSuccess?: () => void;
}

const EditTeamForm = ({ team, onSuccess }: EditTeamFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [captain, setCaptain] = useState<Participant>(team.captain);

  const form = useForm({
    values: {
      title: team.title,
      captain: { id: team.captain.id },
    },
  });

  const handleCaptainChange = (participant: Participant) => {
    form.set('captain.id', participant.id);
    setCaptain(participant);
  };

  const [editTeam, { error, loading }] = useMutation(EditBackendTeamMutation, {
    variables: {
      input: {
        id: team.id,
        ...form.values,
      },
    },
    onCompleted: () => {
      onSuccess?.();
      notifier.success(t('notifications.team_edited'));
    },
    onError: () => {
      //
    },
  });

  const { errors, valid } = useValidation({
    schema: editTeamSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editTeam()}>
      <UI.FormGrid>
        <UI.H4>{t('edit_team')}</UI.H4>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="TeamName">
            {t('team_name')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput {...form.text('title')} maxLength={100} id="TeamName" />
          <UI.ErrorMessages attribute={t('team_name')} errors={errors.title} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.captain, touched: form.touched.captain }}>
          <UI.InputLabel>
            {t('team_captain')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <ContactPicker
            participant={captain}
            onChange={handleCaptainChange}
            nullable={false}
          />
          <UI.ErrorMessages attribute={t('event_title')} errors={errors.captain} />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_team')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditTeamForm;
